import React from 'react'

import { Button, Modal, ModalInjectedProps } from '@sketch/components'
import { useArchiveProjectMutation } from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { removeFromPaginated, removeQuery } from '@sketch/modules-common'
import { useEventDispatch } from '@sketch/utils'

interface ArchiveProjectModalProps extends ModalInjectedProps {
  projectIdentifier: string
  workspaceIdentifier: string
}

const ArchiveProjectModal = (props: ArchiveProjectModalProps) => {
  const { hideModal, projectIdentifier, workspaceIdentifier } = props
  const { showToast } = useToast()

  const dispatchSharesRefresh = useEventDispatch('workspaceShareRefresh')

  const [archiveProject, { loading }] = useArchiveProjectMutation({
    variables: { projectIdentifier },
    onError: 'show-toast',
    onCompleted: ({ archiveProject }) => {
      showToast(`“${archiveProject.project.name}” Archived`)
      hideModal()
    },
    update: cache => {
      // Remove the project from the sidebar Query
      removeFromPaginated(
        cache,
        { __typename: 'Project', identifier: projectIdentifier },
        key => key.includes('projects')
      )

      // Remove from cache every query related with this workspace shares
      // This way the view will be refetched from scratch if needed to make sure
      // we always get the correct server information
      //
      // "key.includes('"archived":"ARCHIVED"')"
      // We also remove the archived listing to make sure it's refreshed
      removeQuery(
        cache,
        key =>
          key.includes(workspaceIdentifier) &&
          (key.includes('.shares') || key.includes('"archived":"ARCHIVED"'))
      )

      // Force any visible query to refresh
      dispatchSharesRefresh({
        workspaceIdentifier,
        projectShortId: projectIdentifier,
      })
    },
  })

  return (
    <Modal onCancel={hideModal}>
      <Modal.Header>Archive Project?</Modal.Header>
      <Modal.Body>
        Your project and containing documents would be sent to Archive. You can
        retrieve it back whenever you need it.
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => archiveProject()}
          loading={loading}
        >
          Archive
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ArchiveProjectModal
